import { useEffect, useState } from 'react'
import Image from 'next/legacy/image'
import Link from 'next/link'
import cn from 'classnames'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
// Components
import LandingCover from '@/components/landings/LandingCover'
import Featured from '@/components/landings/Featured'
import How from '@/components/home/How'
import CustomerReview from '@/components/search/CustomerReview'
import PuppyQuizBanner from '@/components/search/PuppyQuizBanner'
import Why from '@/components//home/Why'
import Button from '@/components/newButton/Button'
import OurPartners from '@/components/ourPartners'
// Utils
import description from '@/components/search/lib/category-description'
import { getBreedImage } from '@/components/home/lib/utils'
import useMediaQuery from '@/utils/publicApi'
import { categoryTitleConverter } from '@/utils/converter'
import styles from './CategoryLanding.module.scss'
import AboutCategories, {
  affordableParagraphs,
  doodleImages,
  doodleParagraphs,
  miniImages,
  miniParagraphs,
} from './about/AboutCategories'

const AboutCategory = ({ variables, numberImg }) => {
  const imgAltMap = {
    doodle: 'Beautiful tan and white Goldendoodle puppy smiling at the camera.',
    mini: 'Yorkshire Terriers posing for a family photo.',
  }

  const category = variables?.category
  const listDescHTML = ['emotional_support', 'therapy', 'service']
  const completeDesc = description[category]
  const descHTML = description[`${category}String`]

  const [allDescription, setAllDescription] = useState(false)
  const shortDescription = listDescHTML.includes(category)
    ? descHTML?.slice(0, 529)
    : completeDesc?.slice(0, 529) + '...'
  const isShortDescription = listDescHTML.includes(category)
    ? descHTML?.length < 600
    : completeDesc?.length < 600

  useEffect(() => {
    isShortDescription ? setAllDescription(true) : setAllDescription(false)
  }, [completeDesc])

  return (
    <div className={styles.aboutContainer}>
      <div className={styles.aboutCategoryBox}>
        <div className={styles.aboutData}>
          <h2>About {categoryTitleConverter(category)}</h2>
          <p>{allDescription ? completeDesc : shortDescription}</p>
          {isShortDescription ? null : allDescription ? (
            <div
              className={styles.view}
              onClick={() => setAllDescription(!allDescription)}
            >
              Read Less <span className={styles.arrowUp} />
            </div>
          ) : (
            <div
              className={styles.view}
              onClick={() => setAllDescription(!allDescription)}
            >
              Read More <span className={styles.arrowDown} />
            </div>
          )}
        </div>
        <div className={styles.aboutImageContainer}>
          <Swiper
            modules={[Autoplay]}
            initialSlide={0}
            grabCursor
            autoplay={{
              delay: 5000,
              disableOnInteraction: true,
            }}
            spaceBetween={30}
            slidesPerView={1}
            pagination={{
              clickable: true,
            }}
          >
            {numberImg.map((number, i) => {
              return (
                <SwiperSlide key={i}>
                  <Image
                    height={200}
                    width={300}
                    className={styles.aboutImage}
                    src={`/landing/category/about/${category + number}.png`}
                    objectFit="cover"
                    layout="responsive"
                    alt={imgAltMap[category] || 'Category Dog Image'}
                    loading="lazy"
                    placeholder="blur"
                    blurDataURL={`/_next/image?url=${category + number}&w=16&q=1`}
                  />
                </SwiperSlide>
              )
            })}
          </Swiper>
        </div>
      </div>
    </div>
  )
}

const BreedSize = ({ dogs }) => {
  const isMobile = useMediaQuery('(max-width:600px)')
  const breeds = dogs?.map((dog) => dog.breed)
  // filter out duplicates
  const slugs = breeds?.map((breed) => breed.slug)
  const filteredDuplicatesBreedsNames = breeds?.filter(
    ({ slug }, i) => !slugs.includes(slug, i + 1),
  )
  const [filterSize, setFilterSize] = useState(filteredDuplicatesBreedsNames)
  const [viewButtonFilter, setViewButtonFilter] = useState(filterSize)
  const [isSelected, setIsSelected] = useState(0)
  useEffect(() => {
    setViewButtonFilter(filterSize.slice(0, 10))
  }, [filterSize])

  function handleClick(e) {
    const size = Number(e.target.value)
    setIsSelected(size)
    if (size === 0) {
      setFilterSize(filteredDuplicatesBreedsNames)
    } else {
      setFilterSize(
        filteredDuplicatesBreedsNames.filter((dog) => dog.size === size),
      )
    }
  }
  const allSizeNumber = filteredDuplicatesBreedsNames.length
  const extraSmallSizeNumber = filteredDuplicatesBreedsNames.filter(
    (dog) => dog.size === 1,
  ).length
  const smallSizeNumber = filteredDuplicatesBreedsNames.filter(
    (dog) => dog.size === 2,
  ).length
  const mediumSizeNumber = filteredDuplicatesBreedsNames.filter(
    (dog) => dog.size === 3,
  ).length
  const largeSizeNumber = filteredDuplicatesBreedsNames.filter(
    (dog) => dog.size === 4,
  ).length

  return (
    <div className={styles.sizeContainer}>
      <div className={styles.title}>
        <h2>What size breed fits your lifestyle best?</h2>
      </div>
      <div className={styles.test}>
        <div className={styles.sizeButtons}>
          <button
            className={cn(
              styles.sizeButton,
              isSelected === 0 && styles.selected,
            )}
            value={0}
            onClick={handleClick}
          >
            {' '}
            All sizes ({allSizeNumber})
          </button>
          <button
            className={cn({
              [styles.sizeButton]: extraSmallSizeNumber > 0,
              [styles.disabled]: extraSmallSizeNumber === 0,
              [styles.selected]: isSelected === 1,
            })}
            value={1}
            onClick={handleClick}
          >
            {' '}
            Extra small ({extraSmallSizeNumber})
          </button>
          <button
            className={cn({
              [styles.sizeButton]: smallSizeNumber > 0,
              [styles.disabled]: smallSizeNumber === 0,
              [styles.selected]: isSelected === 2,
            })}
            value={2}
            onClick={handleClick}
          >
            {' '}
            Small ({smallSizeNumber})
          </button>
          <button
            className={cn({
              [styles.sizeButton]: mediumSizeNumber > 0,
              [styles.disabled]: mediumSizeNumber === 0,
              [styles.selected]: isSelected === 3,
            })}
            value={3}
            onClick={handleClick}
          >
            {' '}
            Medium ({mediumSizeNumber})
          </button>
          <button
            className={cn({
              [styles.sizeButton]: largeSizeNumber > 0,
              [styles.disabled]: largeSizeNumber === 0,
              [styles.selected]: isSelected === 4,
            })}
            value={4}
            onClick={handleClick}
          >
            {' '}
            Large ({largeSizeNumber})
          </button>
        </div>
      </div>
      <div className={styles.sizeSelected}>
        {isMobile ? (
          <Swiper
            initialSlide={0}
            grabCursor
            autoplay={{
              delay: 2000,
              disableOnInteraction: true,
            }}
            slidesPerView="auto"
            spaceBetween={10}
            pagination={{
              clickable: true,
            }}
          >
            {viewButtonFilter?.map((breed, i) => {
              return (
                <SwiperSlide key={i}>
                  <div className={styles.breed}>
                    <Link href={`/search?breed=${breed.slug}`} prefetch={false}>
                      <div
                        style={{
                          backgroundImage: `url(${process.env.NEXT_PUBLIC_MEDIA_BUCKET}${getBreedImage(breed)})`,
                        }}
                        className={styles.profilePicture}
                      />
                    </Link>
                    <Link href={`/search?breed=${breed.slug}`} prefetch={false}>
                      <h4 className={styles.breedName}>{breed.name}</h4>
                    </Link>
                  </div>
                </SwiperSlide>
              )
            })}
          </Swiper>
        ) : (
          viewButtonFilter?.map((breed, i) => {
            return (
              <div key={i} className={styles.breed}>
                <Link href={`/search?breed=${breed.slug}`} prefetch={false}>
                  <div
                    style={{
                      backgroundImage: `url(${process.env.NEXT_PUBLIC_MEDIA_BUCKET}${getBreedImage(breed)})`,
                    }}
                    className={styles.profilePicture}
                  />
                </Link>
                <Link href={`/search?breed=${breed.slug}`} prefetch={false}>
                  <h4 className={styles.breedName}>{breed.name}</h4>
                </Link>
              </div>
            )
          })
        )}
        <div />
      </div>
      {filterSize?.length > 6 && (
        <div className={styles.viewButton}>
          <Button
            title={viewButtonFilter.length > 6 ? 'View less' : 'View more'}
            theme={['black', 'medium']}
            rightImage="/search/arrow-down-black.svg"
            onClick={() =>
              viewButtonFilter.length > 6
                ? setViewButtonFilter(viewButtonFilter.slice(0, 6))
                : setViewButtonFilter(filteredDuplicatesBreedsNames)
            }
          />
        </div>
      )}
    </div>
  )
}

const CategoryLanding = ({ countDogs, variables, landingType, dogs }) => {
  // Database Category Name handling
  const categoryName = categoryTitleConverter(variables?.category)
  const numberImg = [1, 2, 3, 4, 5]

  const dataCategory = {
    doodle: [doodleParagraphs, doodleImages],
    small: [miniParagraphs, miniImages],
    affordable: [affordableParagraphs],
  }

  return (
    <>
      <LandingCover
        category={variables?.category}
        categoryName={categoryName}
        landingType={landingType}
        variables={variables}
      />
      <Featured
        title={`Featured ${categoryName}`}
        dogs={dogs}
        countDogs={countDogs}
        categoryName={categoryName}
        landingType={landingType}
        variables={variables}
      />

      <div className={styles.section}>
        <CustomerReview />
      </div>
      <How />
      <Why />
      <div className={styles.largeSection}>
        <OurPartners />
        <PuppyQuizBanner />
      </div>

      <AboutCategory variables={variables} numberImg={numberImg} />
      {variables?.category !== 'small' && <BreedSize dogs={dogs} />}
      {dataCategory[variables?.category] ? (
        <AboutCategories
          paragraphs={dataCategory[variables?.category][0]}
          images={dataCategory[variables?.category][1]}
        />
      ) : null}
    </>
  )
}

export default CategoryLanding
